import React from 'react';
import { cn } from '../../utils/cn';

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'destructive' | 'outline' | 'link';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  className?: string;
  children: React.ReactNode;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'default', ...props }, ref) => {
    return (
      <button
        className={cn(
          // Base styles
          'inline-flex items-center justify-center gap-2 rounded-pill font-medium transition',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-rm-accent-500 focus-visible:ring-offset-2',
          'disabled:pointer-events-none disabled:opacity-50',
          
          // Variants
          {
            // Primary - Gold gradient CTA
            'text-rm-ink-900 shadow-soft bg-rm-cta hover:brightness-95 active:translate-y-px': variant === 'primary',
            // Secondary - Solid navy
            'text-white bg-rm-primary-700 hover:brightness-110 shadow-soft': variant === 'secondary',
            // Ghost - Navy text on light
            'text-rm-primary-700 hover:bg-rm-bg-50': variant === 'ghost',
            // Destructive - Red for dangerous actions
            'bg-red-600 text-white hover:bg-red-700': variant === 'destructive',
            // Outline - Border with navy text
            'border border-rm-neutral-600/30 bg-white text-rm-primary-700 hover:bg-rm-bg-50': variant === 'outline',
            // Link - Styled as link
            'text-rm-accent-500 hover:text-rm-accent-600 underline underline-offset-4': variant === 'link',
          },
          
          // Sizes
          {
            'px-5 py-3 text-sm': size === 'default',
            'px-3 py-2 text-xs': size === 'sm',
            'px-8 py-4 text-base': size === 'lg',
            'h-10 w-10 p-0': size === 'icon',
          },
          
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = 'Button';

export { Button };
export type { ButtonProps };
